import { useState, useRef, useCallback } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { playActivityUploadApi } from '@/services/playActivityUploadApi';
import { getFileTypeError, getFileExtension, uploadToS3WithRetry } from '@/utils/imageUtils';

interface ActivityImageValue {
  documentId: number;
  viewUrl: string;
}

type ImageUploadApi = Pick<typeof playActivityUploadApi, 'createDocument' | 'markAsUploaded'>;

interface ActivityImageUploadProps {
  value: ActivityImageValue | null;
  onChange: (image: ActivityImageValue | null) => void;
  uploadApi?: ImageUploadApi;
}

/**
 * Activity Image Upload Component
 *
 * Handles direct image upload for play activities (no cropping).
 * Accepts PNG/WebP only to preserve transparency.
 * Max file size: 350 KB.
 *
 * Supports both click-to-browse and drag-and-drop (including drag-to-replace
 * when an image already exists).
 */
export default function ActivityImageUpload({
  value,
  onChange,
  uploadApi,
}: ActivityImageUploadProps) {
  const api = uploadApi ?? playActivityUploadApi;
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks nested dragenter/dragleave so the highlight doesn't flicker when
  // moving over child elements.
  const dragDepthRef = useRef(0);

  const processFile = useCallback(
    async (file: File) => {
      // Validate file type
      const typeError = getFileTypeError(file);
      if (typeError) {
        toast.error(typeError);
        return;
      }

      // Validate file size (350 KB max)
      const fileSizeKB = file.size / 1024;
      if (fileSizeKB > 350) {
        toast.error('Image must be 350 KB or smaller');
        return;
      }

      try {
        setIsUploading(true);

        // 1. Create document and get signed URL
        const fileType = getFileExtension(file.type);
        const document = await api.createDocument({
          fileType,
          mime: file.type, // Preserve original mime type for transparency
        });

        // 2. Upload to S3 with retry logic (passing actual content type)
        await uploadToS3WithRetry(document.uploadUrl, file, 3, file.type);

        // 3. Mark as uploaded
        await api.markAsUploaded({
          documentId: parseInt(document.id, 10),
        });

        // 4. Update parent component
        onChange({
          documentId: parseInt(document.id, 10),
          viewUrl: document.viewUrl,
        });

        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    },
    [onChange, api]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        void processFile(file);
      }
      // Reset so selecting the same file again re-triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processFile]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    // Required so the browser allows the drop instead of opening the file.
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepthRef.current = 0;
      setIsDragging(false);

      const file = event.dataTransfer.files?.[0];
      if (file) {
        void processFile(file);
      }
    },
    [processFile]
  );

  const dropZoneHandlers = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  const highlightClasses = isDragging
    ? 'border-pine-pine bg-pine-hover-item'
    : 'border-pine-line';

  return (
    <div className="space-y-2">
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload UI */}
      {!value ? (
        <Button
          type="button"
          variant="outline"
          onClick={handleUploadClick}
          disabled={isUploading}
          {...dropZoneHandlers}
          className={`w-full h-24 border-2 border-dashed transition-colors ${highlightClasses} ${
            isDragging ? '' : 'hover:border-pine-pine'
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-pine-ink-soft" />
              <span className="text-sm text-pine-ink-soft">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-6 h-6 text-pine-ink-soft" />
              <span className="text-sm font-medium text-pine-ink">
                {isDragging ? 'Drop image here' : 'Upload image'}
              </span>
              <span className="text-xs text-pine-ink-soft">PNG or WebP, max 350 KB</span>
            </div>
          )}
        </Button>
      ) : (
        <div
          {...dropZoneHandlers}
          className={`space-y-2 rounded-md border-2 border-dashed p-2 transition-colors ${highlightClasses}`}
        >
          {/* Preview */}
          <div className="relative">
            <img
              src={value.viewUrl}
              alt="Activity"
              className="w-24 h-24 object-contain rounded-md border border-pine-line bg-pine-paper"
            />
          </div>
          {/* Replace button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUploadClick}
            disabled={isUploading}
            className="text-xs"
          >
            {isUploading ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Uploading...
              </span>
            ) : (
              'Replace image'
            )}
          </Button>
          {isDragging && !isUploading && (
            <p className="text-xs text-pine-pine font-medium">
              Drop to replace image
            </p>
          )}
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-pine-ink-soft">
        PNG or WebP with transparent background. Max 350 KB. Shown at 80×80 in the app.
      </p>
    </div>
  );
}
